import { auth } from '../firebase/config';

export interface ChangePasswordRequest {
    newPassword: string;
}

export interface ChangePasswordResponse {
    success: boolean;
    message: string;
}

/**
 * Change the password for the currently authenticated user
 * Uses the PHP endpoint configured in REACT_APP_CHANGE_PASSWORD_ENDPOINT
 */
export async function changePassword(newPassword: string): Promise<ChangePasswordResponse> {
    const endpoint = process.env.REACT_APP_CHANGE_PASSWORD_ENDPOINT;
    
    if (!endpoint) {
        throw new Error('REACT_APP_CHANGE_PASSWORD_ENDPOINT is not configured in .env file');
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('No user is currently logged in');
    }

    // Get the current user's ID token
    const idToken = await currentUser.getIdToken();

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            idToken,
            newPassword,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to change password');
    }

    return {
        success: data.success || true,
        message: data.message || 'Password changed successfully',
    };
}

export interface ResetUserPasswordResponse {
    success: boolean;
    message: string;
}

/**
 * Reset the password for a selected user (Bank-only feature).
 * Uses the PHP endpoint configured in REACT_APP_RESET_USER_PASSWORD_ENDPOINT
 */
export async function resetUserPassword(targetUid: string, newPassword: string): Promise<ResetUserPasswordResponse> {
    const endpoint = process.env.REACT_APP_RESET_USER_PASSWORD_ENDPOINT;

    if (!endpoint) {
        throw new Error('REACT_APP_RESET_USER_PASSWORD_ENDPOINT is not configured in .env file');
    }

    if (!targetUid) {
        throw new Error('Target user is required');
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('No user is currently logged in');
    }

    const idToken = await currentUser.getIdToken();

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            idToken,
            targetUid,
            newPassword,
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to reset user password');
    }

    return {
        success: data.success || true,
        message: data.message || 'Password reset successfully',
    };
}

export interface UpdateUserEmailResponse {
    success: boolean;
    message: string;
}

/**
 * Update email for a target Firebase Auth user (Bank-only feature).
 * Uses the PHP endpoint configured in REACT_APP_UPDATE_USER_EMAIL_ENDPOINT
 */
export async function updateUserEmail(targetUid: string, newEmail: string): Promise<UpdateUserEmailResponse> {
    const endpoint = process.env.REACT_APP_UPDATE_USER_EMAIL_ENDPOINT;

    if (!endpoint) {
        throw new Error('REACT_APP_UPDATE_USER_EMAIL_ENDPOINT is not configured in .env file');
    }

    if (!targetUid) {
        throw new Error('Target user is required');
    }

    if (!newEmail?.trim()) {
        throw new Error('Email is required');
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('No user is currently logged in');
    }

    const idToken = await currentUser.getIdToken();

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            idToken,
            targetUid,
            email: newEmail.trim().toLowerCase(),
        }),
    });

    const data = await response.json();
    if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to update user email');
    }

    return {
        success: data.success || true,
        message: data.message || 'Email updated successfully',
    };
}

export interface DeleteAuthUserResponse {
    success: boolean;
    message?: string;
}

/**
 * Hard-delete a Firebase Auth user (Bank admin / backend).
 * Mirrors other admin endpoints: POST JSON { idToken, targetUid }.
 * Configure REACT_APP_DELETE_USER_AUTH_ENDPOINT on your PHP/API (Admin SDK deleteUser).
 */
export async function deleteAuthUser(targetUid: string): Promise<DeleteAuthUserResponse> {
    const endpoint = process.env.REACT_APP_DELETE_USER_AUTH_ENDPOINT;

    if (!endpoint) {
        throw new Error(
            'REACT_APP_DELETE_USER_AUTH_ENDPOINT is not configured. Add it to .env so staff can be removed from Firebase Auth.'
        );
    }

    if (!targetUid?.trim()) {
        throw new Error('Target user UID is required');
    }

    const currentUser = auth.currentUser;
    if (!currentUser) {
        throw new Error('No user is currently logged in');
    }

    const idToken = await currentUser.getIdToken();

    const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
            idToken,
            targetUid: targetUid.trim(),
        }),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
        throw new Error(data.message || data.error || 'Failed to delete Auth user');
    }

    return {
        success: data.success !== false,
        message: data.message || 'Auth user deleted',
    };
}
